import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { StandardItem, StandardPlace } from '../../types/standard';
import { standardClient } from '../../lib/standard';
import { useHeader } from '../../context/HeaderContext';
import { CategoryFilter } from '../shared/CategoryFilter';
import { ListView } from './ListView';
import MapView from './MapView';
import { ViewToggle } from './ViewToggle';
import { DetailCard } from './DetailCard';
import { PlaceCard } from './PlaceCard';
import { LoadingIndicator } from '../shared/LoadingIndicator';
import { useAirtableQuery } from '../../hooks/useAirtableQuery';
import type { SupportedLanguages } from '../../i18n';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';
import { slugify } from '../../utils/slugify';
import { SectionHeader } from '../layout/SectionHeader';
import { resolveColor } from '../../utils/colorUtils';
import { useSection } from '../../hooks/useSection';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { navigationClient } from '../../lib/navigation';

const StandardSection: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setTitle } = useHeader();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const section = useSection();
  const scrollPositionRef = useRef<number>(0);
  
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [view, setView] = useState<'map' | 'list'>('list');
  const [selectedItem, setSelectedItem] = useState<StandardItem | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<StandardPlace | null>(null);
  const [detailBehavior, setDetailBehavior] = useState<'default' | 'pin' | 'place'>('default');
  const [selectedShowIndex, setSelectedShowIndex] = useState<number | null>(null);

  // Data fetching hooks
  const { data: config, isLoading: configLoading } = useAirtableQuery.useConfig();
  const { data: translations, isLoading: translationsLoading } = useAirtableQuery.useTranslations(i18n.language as SupportedLanguages);

  // Fetch items using section.id
  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useQuery({
    queryKey: ['standard-items', section?.id],
    queryFn: async () => {
      if (!section?.id) {
        throw new Error('Section ID not found');
      }
      return standardClient.getItems(section.id);
    },
    enabled: !!section?.id,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000
  });

  // Track section view
  useEffect(() => {
    if (section?.id) {
      analyticsService.trackSectionView(
        section.id,
        'standard',
        view,
        {
          section_name: section.names.fr || 'Unknown Section'
        }
      );
    }
  }, [section?.id, view]);

  // Category colors memo
  const categoryColors = React.useMemo(() => {
    if (!config || !items.length) return new Map<string, string>();

    const colors = config.colors_map.split(',').map((color: string) => color.trim());
    const uniqueCategories = Array.from(new Set(items.map(item => item.category)));
    const colorMap = new Map<string, string>();

    uniqueCategories.forEach((category, index) => {
      colorMap.set(category, colors[index % colors.length]);
    });

    return colorMap;
  }, [config, items]);

  // Filtered items memo
  const filteredItems = React.useMemo(() => {
    if (!selectedCategory && !selectedType) return items;
    return items.filter(item => {
      const categoryMatch = !selectedCategory || item.category === selectedCategory;
      const typeMatch = !selectedType || item.type === selectedType;
      return categoryMatch && typeMatch;
    });
  }, [items, selectedCategory, selectedType]);

  // Places memo
  const places = React.useMemo(() => {
    return standardClient.getPlaces(filteredItems);
  }, [filteredItems]);

  // Effects
  useEffect(() => {
    if (section?.names) {
      const name = section.names[i18n.language as SupportedLanguages] || section.names.fr;
      setTitle(name);
    }
  }, [section, i18n.language, setTitle]);

  useEffect(() => {
    if (name && items) {
      const item = items.find(i => slugify(i.name) === name);
      if (item) {
        setSelectedItem(item);
      }
    }
  }, [name, items]);

  // Event handlers
  const handleBackClick = () => {
    navigate('/');
  };

  const handleCategorySelect = (category: string) => {
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'category_filter',
      event_data: {
        section_id: section?.id,
        section_name: section?.names?.fr || 'Unknown',
        section_type: 'standard',
        filter_value: category || 'all'
      }
    });
    setSelectedCategory(category);
    setSelectedType('');
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleTypeSelect = (type: string) => {
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'type_filter',
      event_data: {
        section_id: section?.id,
        section_name: section?.names?.fr || 'Unknown',
        section_type: 'standard',
        filter_value: type || 'all'
      }
    });
    const itemWithType = items.find(item => item.type === type);
    if (itemWithType) {
      setSelectedCategory(itemWithType.category);
    }
    setSelectedType(type);
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`);
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analyticsService.trackSectionView(
      section?.id || '',
      'standard',
      newView,
      {
        section_name: section?.names?.fr || 'Unknown Section'
      }
    );
    setView(newView);
  };

  const handleItemClick = (item: StandardItem) => {
    // Save current scroll position before navigating
    scrollPositionRef.current = window.scrollY;
    
    const itemSlug = slugify(item.name);
    analyticsService.trackItemAction(
      section?.id || '',
      item.id,
      'item_click',
      'standard',
      {
        item_name: item.name,
        category: item.category,
        type: item.type,
        section_name: section?.names?.fr || 'Unknown Section',
        section_type: 'standard'
      }
    );
    
    // Utiliser la navigation relative
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}/${itemSlug}`);
    
    setSelectedItem(item);
    setDetailBehavior('default');
    setSelectedPlace(null);
    
    logger.debug('Item clicked, saved scroll position', {
      category: 'Navigation',
      data: {
        scrollPosition: scrollPositionRef.current,
        itemName: item.name,
        sectionName: section?.names?.fr
      }
    });
  };

  const handlePlaceClick = (place: StandardPlace) => {
    analyticsService.trackItemAction(
      section?.id || '',
      place.name,
      'place_card_view',
      'place',
      {
        place_name: place.name,
        items_count: place.items.length,
        section_name: section?.names?.fr || 'Unknown Section',
        section_type: 'standard'
      }
    );
    setSelectedPlace(place);
    setSelectedItem(null);
  };

  const handleEventFromPlace = (event: StandardItem, showIndex: number) => {
    if (!section?.names?.fr) return;

    const sectionSlug = slugify(section.names.fr);
    const itemSlug = slugify(event.name);
    navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
    
    setSelectedItem(event);
    setSelectedShowIndex(showIndex);
    setDetailBehavior('place');
  };

  const handleItemChange = (newItem: StandardItem) => {
    if (!section?.names?.fr) return;

    const sectionSlug = slugify(section.names.fr);
    const itemSlug = slugify(newItem.name);
    
    // Utiliser la navigation relative
    navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
    
    if (detailBehavior === 'place' && selectedPlace) {
      // Find the new index in the shows
      const newIndex = selectedPlace.items.findIndex(item => item.id === newItem.id);
      if (newIndex !== -1) {
        setSelectedItem(newItem);
        setSelectedShowIndex(newIndex);
      }
    } else {
      setSelectedItem(newItem);
    }
  };

  const handleDetailClose = () => {
    // Naviguer vers la section sans paramètre d'item
    const sectionSlug = slugify(section?.names.fr || '');
    navigate(`/${sectionSlug}`, { replace: true });
    
    setSelectedItem(null);
    setSelectedPlace(null);
    
    // Restore scroll position after a short delay to allow for navigation
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
      
      logger.debug('Detail card closed, restored scroll position', {
        category: 'Navigation',
        data: {
          scrollPosition: scrollPositionRef.current,
          sectionName: section?.names?.fr
        }
      });
    }, 50);
  };

  // Loading state
  if (configLoading || translationsLoading) {
    return <LoadingIndicator />;
  }

  // Error state
  if (!config || !translations || !section) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">
          {itemsError instanceof Error ? itemsError.message : 'Erreur lors du chargement des données'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: resolveColor(config['sectionBack-color'], config, '#f9fafb')
      }}
    >
      <SectionHeader
        config={config}
        title={section.names[i18n.language as SupportedLanguages] || section.names.fr}
        onBackClick={handleBackClick}
        translations={translations}
      />

      {/* Filter */}
      <div className="filter-block" style={{ backgroundColor: config['major-color'] }}>
        <CategoryFilter
          selectedCategory={selectedCategory}
          selectedType={selectedType}
          onCategoryChange={handleCategorySelect}
          onTypeChange={handleTypeSelect}
          config={config}
          translations={translations}
          items={items}
        />
      </div>

      {/* Main content */}
      <main className="main-content">
        {view === 'list' ? (
          <ListView
            items={filteredItems}
            onItemClick={handleItemClick}
            onCategorySelect={handleCategorySelect}
            onTypeSelect={handleTypeSelect}
            config={config}
            translations={translations}
            categoryColors={categoryColors}
          />
        ) : (
          <div 
            className="fixed inset-0 pt-[94px] pb-[40px]"
            style={{ zIndex: 1 }}
          >
            <MapView
              places={places}
              config={config}
              translations={translations}
              onItemClick={handleItemClick}
              onPlaceClick={handlePlaceClick}
              mainLocation={{
                name: config.name,
                latitude: config.latitude,
                longitude: config.longitude,
                fulladress: config.fulladress
              }}
              categoryColors={categoryColors}
              onCategorySelect={handleCategorySelect}
              onTypeSelect={handleTypeSelect}
              mapInstanceRef={mapInstanceRef}
            />
          </div>
        )}
      </main>

      {/* Fixed view toggle button */}
      <div 
        className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[58px]"
      >
        <ViewToggle
          view={view}
          onChange={handleViewChange}
          config={config}
          translations={translations}
        />
      </div>

      {/* Modals */}
      {selectedPlace && !selectedItem && (
        <PlaceCard
          place={selectedPlace}
          onClose={() => setSelectedPlace(null)}
          onEventClick={(item) => {
            const itemSlug = slugify(item.name);
            const sectionSlug = slugify(section.names.fr);
            navigate(`/${sectionSlug}/${itemSlug}`, { replace: true });
            setSelectedItem(item);
            setSelectedPlace(null);
          }}
          onCategorySelect={handleCategorySelect}
          onTypeSelect={handleTypeSelect}
          config={config}
          translations={translations}
          categoryColors={categoryColors}
          selectedDate=""
        />
      )}

      {selectedItem && (
        <DetailCard
          item={selectedItem}
          items={filteredItems}
          onClose={handleDetailClose}
          onItemChange={handleItemChange}
          onCategorySelect={handleCategorySelect}
          onTypeSelect={handleTypeSelect}
          config={config}
          translations={translations}
          tableId={section.id}
          categoryColors={categoryColors}
        />
      )}
    </div>
  );
};

export default StandardSection;
export { StandardSection };